import { useState } from "react";
import { Link } from "@tanstack/react-router";

import { COLOR_LAB_BUTTON_DEMOS, MONOLITH_BOARD_STYLE } from "@/components/dashboard/monolithBoardStyle";
import { Button } from "@/components/ui/Button";
import { EngravedTabs, type EngravedTab } from "@/components/ui/EngravedTabs";
import { SideNav, type SideNavItem } from "@/components/ui/SideNav";
import { useToast } from "@/components/ui/ToastProvider";
import { cn } from "@/lib/classNames";

const SAMPLE_TABS: EngravedTab[] = [
  { id: "overview", label: "Overview" },
  { id: "encounters", label: "Encounters" },
  { id: "loot", label: "Loot" },
];

const SAMPLE_NAV_ITEMS: SideNavItem[] = [
  { href: "#", label: "Dashboard", match: "exact" },
  { href: "#campaigns", label: "Campaigns", match: "prefix" },
  { href: "#settings", label: "Settings", match: "prefix" },
];

export function DesignSystemComponentsClient() {
  const toast = useToast();
  const [activeTabId, setActiveTabId] = useState("overview");

  return (
    <div className="mx-auto min-h-full max-w-5xl px-5 py-12 pb-24 text-neutral-950 sm:px-8">
      <header className="border-b-[3px] border-neutral-950 pb-10">
        <p className="text-[11px] uppercase tracking-[0.28em] text-neutral-600">Design system · Components</p>
        <h1 className="mt-4 text-4xl font-bold leading-tight tracking-tight [font-family:var(--color-lab-display),system-ui,sans-serif] sm:text-5xl">
          UI primitives
        </h1>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-neutral-800">
          Production controls and notifications. Tile textures, overlay packs, and catalog assets are authored in
          admin; this page links there for visual review alongside app chrome.
        </p>
      </header>

      <div className="mt-14 space-y-20">
        <section className="space-y-6" aria-labelledby="ds-buttons">
          <h2
            id="ds-buttons"
            className="scroll-mt-6 text-xl font-semibold uppercase tracking-[0.2em] [font-family:var(--color-lab-display),system-ui,sans-serif]"
          >
            Button
          </h2>
          <p className="max-w-2xl text-neutral-800">
            Same semantic recipes as the color lab, rendered here for quick comparison next to toasts and map tooling
            links.
          </p>
          <div className="grid gap-6 border-[3px] border-neutral-950 bg-[#fffaf1] p-6 shadow-[10px_10px_0_rgba(19,19,19,0.12)] sm:grid-cols-2 lg:grid-cols-3 sm:p-8">
            {COLOR_LAB_BUTTON_DEMOS.map((demo) => (
              <div key={demo.id} className="space-y-3">
                <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-neutral-500">
                  {demo.title}
                </p>
                <Button variant={demo.variant} chrome={demo.chrome} type="button">
                  Sample
                </Button>
                <p className="text-xs leading-relaxed text-neutral-600">{demo.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-6" aria-labelledby="ds-card">
          <h2
            id="ds-card"
            className="scroll-mt-6 text-xl font-semibold uppercase tracking-[0.2em] [font-family:var(--color-lab-display),system-ui,sans-serif]"
          >
            Panel + actions
          </h2>
          <p className="max-w-2xl text-neutral-800">
            <code className="rounded bg-neutral-200/80 px-1.5 py-0.5 font-mono text-[0.85em]">
              MONOLITH_BOARD_STYLE.panelClassName
            </code>{" "}
            with framed primary and cancel actions. The semantic button rules are documented on the color lab route.
          </p>
          <article
            className={cn(
              "flex max-w-xl flex-col rounded-[28px] p-6 sm:p-7",
              MONOLITH_BOARD_STYLE.panelClassName,
            )}
          >
            <p className="text-[10px] uppercase tracking-[0.28em] text-neutral-600">Sample panel</p>
            <h3 className="mt-2 text-xl font-bold uppercase tracking-tight [font-family:var(--color-lab-display),system-ui,sans-serif]">
              Session notes
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-neutral-800">
              Short body copy on paper. Primary confirms the main outcome; cancel backs out without competing with the
              green CTA.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3 border-t-2 border-neutral-950/12 pt-6">
              <Button variant="primary" chrome="frame" type="button">
                Save notes
              </Button>
              <Button variant="cancel" chrome="frame" type="button">
                Cancel
              </Button>
            </div>
          </article>
        </section>

        <section className="space-y-6" aria-labelledby="ds-toast">
          <h2
            id="ds-toast"
            className="scroll-mt-6 text-xl font-semibold uppercase tracking-[0.2em] [font-family:var(--color-lab-display),system-ui,sans-serif]"
          >
            Notifications (toast)
          </h2>
          <p className="max-w-2xl text-neutral-800">
            Fixed bottom-right stack from{" "}
            <code className="rounded bg-neutral-200/80 px-1.5 py-0.5 font-mono text-[0.85em]">ToastProvider</code>
            . Trigger samples below; each dismisses on its own timer or via Close.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="muted"
              chrome="frame"
              type="button"
              onClick={() => toast.info("Your draft is still available on this device.")}
            >
              Info toast
            </Button>
            <Button
              variant="primary"
              chrome="frame"
              type="button"
              onClick={() => toast.success("Campaign saved to the cloud.")}
            >
              Success toast
            </Button>
            <Button
              variant="destructive"
              chrome="frame"
              type="button"
              onClick={() => toast.error("Could not reach the server. Try again in a moment.")}
            >
              Error toast
            </Button>
          </div>
        </section>

        <section className="space-y-6" aria-labelledby="ds-tabs">
          <h2
            id="ds-tabs"
            className="scroll-mt-6 text-xl font-semibold uppercase tracking-[0.2em] [font-family:var(--color-lab-display),system-ui,sans-serif]"
          >
            Engraved tabs
          </h2>
          <p className="max-w-2xl text-neutral-800">
            Tab switcher with rounded segments: inactive tabs get a light neutral fill on hover; the
            active tab uses a slightly stronger neutral tint, with a darker hover on top of that.
            Pass an array of tabs, an active ID, and an onSelect callback.
          </p>
          <div className="overflow-hidden border-[3px] border-neutral-950 bg-[#fffaf1] shadow-[10px_10px_0_rgba(19,19,19,0.12)]">
            <EngravedTabs tabs={SAMPLE_TABS} activeId={activeTabId} onSelect={setActiveTabId} />
            <div className="p-6 text-sm leading-relaxed text-neutral-800">
              Content for <strong className="font-semibold text-neutral-950">{SAMPLE_TABS.find((t) => t.id === activeTabId)?.label}</strong>.
              Click the tabs above to see the active and hover fills.
            </div>
          </div>
        </section>

        <section className="space-y-6" aria-labelledby="ds-sidenav">
          <h2
            id="ds-sidenav"
            className="scroll-mt-6 text-xl font-semibold uppercase tracking-[0.2em] [font-family:var(--color-lab-display),system-ui,sans-serif]"
          >
            Side nav
          </h2>
          <p className="max-w-2xl text-neutral-800">
            Vertical navigation menu with ink-filled active state and offset shadow. Used by the
            design system sidebar. Pass an array of items with href, label, and match strategy, plus
            the current pathname.
          </p>
          <div className="max-w-xs border-[3px] border-neutral-950 bg-[#fffaf1] p-5 shadow-[10px_10px_0_rgba(19,19,19,0.12)]">
            <SideNav
              items={SAMPLE_NAV_ITEMS}
              pathname="#"
              label="Sample navigation"
            />
          </div>
        </section>

        <section className="space-y-6" aria-labelledby="ds-map-assets">
          <h2
            id="ds-map-assets"
            className="scroll-mt-6 text-xl font-semibold uppercase tracking-[0.2em] [font-family:var(--color-lab-display),system-ui,sans-serif]"
          >
            Map UI assets
          </h2>
          <p className="max-w-2xl text-neutral-800">
            Hex tiles, overlay sprites, and system catalog entries are not embedded in this gallery — open the admin
            workspaces to preview and edit stored assets.
          </p>
          <ul className="flex flex-col gap-3 text-sm">
            <li>
              <Link
                to="/admin/tiles"
                className="font-medium underline decoration-2 underline-offset-4 hover:text-neutral-700"
              >
                Tile workshop
              </Link>
              <span className="text-neutral-600"> — texture authoring and catalog hooks</span>
            </li>
            <li>
              <Link
                to="/admin/overlays"
                className="font-medium underline decoration-2 underline-offset-4 hover:text-neutral-700"
              >
                Overlay workshop
              </Link>
              <span className="text-neutral-600"> — packs, markers, roads, coast pieces</span>
            </li>
            <li>
              <Link
                to="/admin/system-catalog"
                className="font-medium underline decoration-2 underline-offset-4 hover:text-neutral-700"
              >
                System catalog
              </Link>
              <span className="text-neutral-600"> — taxonomy and storage-backed reference</span>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}
